/**
 * Font Loading and Measurement Utilities
 * 
 * Extracted from ClassicCaptionPage.tsx to provide professional font handling
 * for timeloom text rendering systems.
 * 
 * Features:
 * - Font loading management with error handling
 * - Precise canvas-based text measurement
 * - Font family mapping and resolution
 * - Integration with clipCreator font system
 * - Cross-platform font fallback handling
 * - Word positioning calculation for multi-word text
 * - Typography spacing and kerning utilities
 */

import React, { useEffect, useMemo, useState } from 'react';

/**
 * Font configuration interface
 */
export interface FontConfig {
    name: string;
    family: string;
    url?: string;
    fallbacks?: string[];
    loaded?: boolean;
}

/**
 * Text measurement result interface
 */
export interface TextMeasurement {
    width: number;
    height: number;
    actualBoundingBoxLeft: number;
    actualBoundingBoxRight: number;
    actualBoundingBoxAscent: number;
    actualBoundingBoxDescent: number;
}

/**
 * Word positioning for multi-word text layout
 */
export interface WordPosition {
    x: number;
    width: number;
    text: string;
    index: number;
}

/**
 * Font loading result
 */
export interface FontLoadResult {
    success: boolean;
    fontFamily: string;
    error?: string;
}

/**
 * Default font map - will be enhanced to integrate with clipCreator font system
 */
export const DEFAULT_FONT_MAP: { [key: string]: string } = {
    'Arial': 'Arial, sans-serif',
    'Helvetica': 'Helvetica, Arial, sans-serif',
    'Times New Roman': '"Times New Roman", Times, serif',
    'Georgia': 'Georgia, Times, serif',
    'Verdana': 'Verdana, Geneva, sans-serif',
    'Trebuchet MS': '"Trebuchet MS", Helvetica, sans-serif',
    'Comic Sans MS': '"Comic Sans MS", cursive',
    'Impact': 'Impact, "Arial Black", sans-serif',
    'Lucida Console': '"Lucida Console", Monaco, monospace',
    'Tahoma': 'Tahoma, Geneva, sans-serif',
    'Courier New': '"Courier New", Courier, monospace',
    'Palatino': '"Palatino Linotype", "Book Antiqua", Palatino, serif'
};

/**
 * Professional font categories for better organization
 */
export const FONT_CATEGORIES = {
    serif: ['Times New Roman', 'Georgia', 'Palatino'],
    sans_serif: ['Arial', 'Helvetica', 'Verdana', 'Trebuchet MS', 'Tahoma'],
    monospace: ['Courier New', 'Lucida Console'],
    display: ['Impact', 'Comic Sans MS'],
    system: ['system-ui', '-apple-system', 'BlinkMacSystemFont']
} as const;

/**
 * Font loading manager class
 */
class FontManager {
    private loadedFonts: Set<string> = new Set();
    private loadingPromises: Map<string, Promise<FontLoadResult>> = new Map();
    private fontConfigs: Map<string, FontConfig> = new Map();

    constructor() {
        // Initialize with default fonts
        Object.entries(DEFAULT_FONT_MAP).forEach(([name, family]) => {
            this.fontConfigs.set(name, {
                name,
                family,
                loaded: true // System fonts are considered loaded
            });
        });
    }

    /**
     * Register a custom font configuration
     */
    registerFont(config: FontConfig): void {
        this.fontConfigs.set(config.name, config);
    }

    /**
     * Load a font asynchronously with error handling
     */
    async loadFont(fontName: string): Promise<FontLoadResult> {
        // Check if already loaded
        if (this.loadedFonts.has(fontName)) {
            return {
                success: true,
                fontFamily: this.resolveFontFamily(fontName)
            };
        }

        // Check if loading is in progress
        if (this.loadingPromises.has(fontName)) {
            return this.loadingPromises.get(fontName)!;
        }

        // Start loading process
        const loadingPromise = this._loadFontInternal(fontName);
        this.loadingPromises.set(fontName, loadingPromise);

        const result = await loadingPromise;
        this.loadingPromises.delete(fontName);

        if (result.success) {
            this.loadedFonts.add(fontName);
        }

        return result;
    }

    /**
     * Internal font loading implementation
     */
    private async _loadFontInternal(fontName: string): Promise<FontLoadResult> {
        const config = this.fontConfigs.get(fontName);
        
        if (!config) {
            return {
                success: false,
                fontFamily: DEFAULT_FONT_MAP[fontName] || 'Arial',
                error: `Font configuration not found: ${fontName}`
            };
        }

        // If it's a system font, consider it loaded
        if (!config.url) {
            return {
                success: true,
                fontFamily: config.family
            };
        }

        // Load custom font via CSS Font Loading API
        try {
            if (typeof document !== 'undefined' && 'fonts' in document) {
                const font = new FontFace(config.name, `url(${config.url})`);
                await font.load();
                document.fonts.add(font);
                
                return {
                    success: true,
                    fontFamily: config.family
                };
            } else {
                // Fallback for environments without Font Loading API
                return await this._loadFontViaStylesheet(config);
            }
        } catch (error) {
            return {
                success: false,
                fontFamily: this._getFallbackFamily(config),
                error: `Failed to load font: ${error}`
            };
        }
    }

    /**
     * Fallback font loading via stylesheet injection
     */
    private async _loadFontViaStylesheet(config: FontConfig): Promise<FontLoadResult> {
        return new Promise((resolve) => {
            if (typeof document === 'undefined') {
                resolve({
                    success: false,
                    fontFamily: this._getFallbackFamily(config),
                    error: 'Document not available'
                });
                return;
            }

            const style = document.createElement('style');
            style.textContent = `
                @font-face {
                    font-family: '${config.name}';
                    src: url('${config.url}');
                    font-display: swap;
                }
            `;
            document.head.appendChild(style);

            // Check if font is actually loaded using modern API
            if (document.fonts) {
                // Modern approach: use document.fonts.ready
                document.fonts.ready.then(() => {
                    const fontFace = new FontFace(config.name, `url('${config.url}')`);
                    fontFace.load().then(() => {
                        resolve({
                            success: true,
                            fontFamily: config.family
                        });
                    }).catch(() => {
                        // Font failed to load, fallback after short delay
                        setTimeout(() => {
                            resolve({
                                success: true,
                                fontFamily: config.family
                            });
                        }, 500);
                    });
                }).catch(() => {
                    // document.fonts.ready failed, fallback with shorter timeout
                    setTimeout(() => {
                        resolve({
                            success: true,
                            fontFamily: config.family
                        });
                    }, 800);
                });
            } else {
                // Fallback for older browsers - reduced timeout
                setTimeout(() => {
                    resolve({
                        success: true,
                        fontFamily: config.family
                    });
                }, 800);
            }
        });
    }

    /**
     * Get fallback font family
     */
    private _getFallbackFamily(config: FontConfig): string {
        if (config.fallbacks && config.fallbacks.length > 0) {
            return config.fallbacks.join(', ');
        }
        return DEFAULT_FONT_MAP['Arial'] || 'Arial, sans-serif';
    }

    /**
     * Resolve font family string for CSS usage
     */
    resolveFontFamily(fontName: string): string {
        const config = this.fontConfigs.get(fontName);
        return config ? config.family : (DEFAULT_FONT_MAP[fontName] || fontName);
    }

    /**
     * Check if a font is loaded
     */
    isFontLoaded(fontName: string): boolean {
        return this.loadedFonts.has(fontName);
    }

    /**
     * Get all available fonts
     */
    getAvailableFonts(): FontConfig[] {
        return Array.from(this.fontConfigs.values());
    }

    /**
     * Load all configured fonts
     */
    async loadAllFonts(): Promise<FontLoadResult[]> {
        const fontNames = Array.from(this.fontConfigs.keys());
        const loadPromises = fontNames.map(name => this.loadFont(name));
        return Promise.all(loadPromises);
    }
}

// Global font manager instance
const fontManager = new FontManager();

/**
 * Canvas-based text measurement utilities
 */
export class TextMeasurer {
    private canvas: HTMLCanvasElement | null = null;
    private context: CanvasRenderingContext2D | null = null;

    constructor() {
        this.initializeCanvas();
    }

    private initializeCanvas(): void {
        if (typeof document !== 'undefined') {
            this.canvas = document.createElement('canvas');
            this.context = this.canvas.getContext('2d');
        }
    }

    /**
     * Measure text dimensions with high precision
     */
    measureText(text: string, fontSize: number, fontFamily: string): TextMeasurement | null {
        if (!this.context) {
            return null;
        }

        // Set font properties
        this.context.font = `${fontSize}px ${fontFamily}`;
        
        // Get measurement data
        const metrics = this.context.measureText(text);
        
        return {
            width: metrics.width,
            height: fontSize, // Approximate height based on font size
            actualBoundingBoxLeft: metrics.actualBoundingBoxLeft || 0,
            actualBoundingBoxRight: metrics.actualBoundingBoxRight || metrics.width,
            actualBoundingBoxAscent: metrics.actualBoundingBoxAscent || fontSize * 0.8,
            actualBoundingBoxDescent: metrics.actualBoundingBoxDescent || fontSize * 0.2
        };
    }

    /**
     * Calculate word positions for multi-word text with proper spacing
     */
    calculateWordPositions(
        words: string[],
        fontSize: number,
        fontFamily: string,
        containerWidth?: number,
        spaceMultiplier: number = 1.2
    ): WordPosition[] {
        if (!this.context) {
            return words.map((word, index) => ({
                x: index * 100, // Fallback positioning
                width: word.length * fontSize * 0.6,
                text: word,
                index
            }));
        }

        this.context.font = `${fontSize}px ${fontFamily}`;
        
        // Calculate space width with multiplier for readability
        const baseSpaceWidth = this.context.measureText(' ').width;
        const spaceWidth = baseSpaceWidth * spaceMultiplier;
        
        // Calculate total width
        const totalTextWidth = words.reduce((acc, word, i) => {
            const wordWidth = this.context!.measureText(word).width;
            return acc + wordWidth + (i > 0 ? spaceWidth : 0);
        }, 0);
        
        // Determine starting position (center if container width provided)
        const startX = containerWidth ? (containerWidth - totalTextWidth) / 2 : 0;
        
        // Calculate individual word positions
        let currentX = startX;
        return words.map((word, index) => {
            if (index > 0) {
                currentX += spaceWidth;
            }

            const width = this.context!.measureText(word).width;
            const position: WordPosition = {
                x: currentX,
                width,
                text: word,
                index
            };
            
            currentX += width;
            return position;
        });
    }

    /**
     * Calculate optimal font size to fit within constraints
     */
    calculateOptimalFontSize(
        text: string,
        fontFamily: string,
        maxWidth: number,
        maxHeight: number,
        minFontSize: number = 12,
        maxFontSize: number = 72
    ): number {
        if (!this.context) {
            return Math.min(maxFontSize, maxWidth / text.length * 1.2); // Rough estimate
        }

        let fontSize = maxFontSize;
        
        while (fontSize >= minFontSize) {
            this.context.font = `${fontSize}px ${fontFamily}`;
            const metrics = this.context.measureText(text);
            
            if (metrics.width <= maxWidth && fontSize <= maxHeight) {
                return fontSize;
            }
            
            fontSize -= 1;
        }
        
        return minFontSize;
    }
}

// Global text measurer instance
const textMeasurer = new TextMeasurer();

/**
 * React hook for font loading
 */
export function useFontLoader(fontNames: string | string[]) {
    const [loadingStates, setLoadingStates] = useState<{ [key: string]: 'loading' | 'loaded' | 'error' }>({});
    
    useEffect(() => {
        const fonts = Array.isArray(fontNames) ? fontNames : [fontNames];
        
        // Initialize loading states
        const initialStates: { [key: string]: 'loading' | 'loaded' | 'error' } = {};
        fonts.forEach(font => {
            initialStates[font] = 'loading';
        });
        setLoadingStates(initialStates);
        
        // Load fonts
        Promise.all(fonts.map(font => fontManager.loadFont(font)))
            .then(results => {
                const newStates: { [key: string]: 'loading' | 'loaded' | 'error' } = {};
                results.forEach((result, index) => {
                    const fontName = fonts[index];
                    newStates[fontName] = result.success ? 'loaded' : 'error';
                });
                setLoadingStates(newStates);
            });
    }, [fontNames]);
    
    return loadingStates;
}

/**
 * Enhanced font loader component that integrates with clipCreator system
 */
export const FontLoader: React.FC<{ 
    fonts?: string | string[];
    children: React.ReactNode;
    fallback?: React.ReactNode;
    initializeClipCreatorFonts?: boolean;
}> = ({ fonts = [], children, fallback = null, initializeClipCreatorFonts = true }) => {
    const [clipCreatorFontsInitialized, setClipCreatorFontsInitialized] = useState(false);
    const fontKey = Array.isArray(fonts) ? fonts.join("|") : (fonts || "");
    const fontArray = useMemo(() => {
        return fontKey ? fontKey.split("|") : [];
    }, [fontKey]);
    const loadingStates = useFontLoader(fontArray);
    
    // Initialize clipCreator fonts on mount
    useEffect(() => {
        let isMounted = true;
        if (initializeClipCreatorFonts) {
            setClipCreatorFontsInitialized(false);
            initializeTimeloomFontSystem(fontArray).then(() => {
                if (isMounted) {
                    setClipCreatorFontsInitialized(true);
                }
            }).catch(error => {
                console.warn('Failed to initialize clipCreator fonts:', error);
                if (isMounted) {
                    setClipCreatorFontsInitialized(true); // Continue anyway
                }
            });
        } else {
            setClipCreatorFontsInitialized(true);
        }
        return () => {
            isMounted = false;
        };
    }, [initializeClipCreatorFonts, fontArray]);
    
    // Check if all fonts are loaded or errored (not loading)
    const allSpecificFontsReady = fontArray.length === 0 || fontArray.every(font => 
        loadingStates[font] === 'loaded' || loadingStates[font] === 'error'
    );
    
    const allSystemsReady = clipCreatorFontsInitialized && allSpecificFontsReady;
    
    if (!allSystemsReady) {
        return fallback || <div style={{ opacity: 0 }}>Loading fonts...</div>;
    }
    
    return <>{children}</>;
};

/**
 * Utility functions export
 */

export function getFontManager(): FontManager {
    return fontManager;
}

export function getTextMeasurer(): TextMeasurer {
    return textMeasurer;
}

/**
 * Convenient functions for common operations
 */

export function measureText(text: string, fontSize: number, fontFamily: string): TextMeasurement | null {
    return textMeasurer.measureText(text, fontSize, fontFamily);
}

export function calculateWordPositions(
    words: string[],
    fontSize: number,
    fontFamily: string,
    containerWidth?: number,
    spaceMultiplier?: number
): WordPosition[] {
    return textMeasurer.calculateWordPositions(words, fontSize, fontFamily, containerWidth, spaceMultiplier);
}

export function resolveFontFamily(fontName: string): string {
    return fontManager.resolveFontFamily(fontName);
}

export async function loadFont(fontName: string): Promise<FontLoadResult> {
    return fontManager.loadFont(fontName);
}

export async function loadAllFonts(): Promise<FontLoadResult[]> {
    return fontManager.loadAllFonts();
}

/**
 * Integration with clipCreator font system
 * Professional Google Fonts integration via Remotion
 */

// Import the clipCreator font system
import { 
    FONT_MAP as CLIP_CREATOR_FONT_MAP, 
    FontValue, 
    FONT_OPTIONS, 
    loadFontsByValue as loadClipCreatorFontsByValue
} from '../../clipCreator/remotion/types/fonts';

export interface ClipCreatorFontConfig {
    name: string;
    value: FontValue;
    url?: string;
    category?: keyof typeof FONT_CATEGORIES;
}

/**
 * Enhanced font map that integrates clipCreator fonts with defaults
 */
export const INTEGRATED_FONT_MAP: { [key: string]: string } = {
    // Default system fonts
    ...DEFAULT_FONT_MAP,
    // ClipCreator Google Fonts (override defaults with better fonts)
    ...Object.entries(CLIP_CREATOR_FONT_MAP).reduce((acc, [key, fontFamily]) => ({
        ...acc,
        [key]: fontFamily,
        // Also register by display name for easier lookup
        [FONT_OPTIONS.find(opt => opt.value === key)?.label || key]: fontFamily
    }), {} as { [key: string]: string })
};

/**
 * Register fonts from clipCreator font system
 */
export function registerClipCreatorFonts(): void {
    Object.entries(CLIP_CREATOR_FONT_MAP).forEach(([fontKey, fontFamily]) => {
        const fontOption = FONT_OPTIONS.find(opt => opt.value === fontKey);
        const displayName = fontOption?.label || fontKey;
        
        fontManager.registerFont({
            name: displayName,
            family: fontFamily,
            loaded: true // Remotion fonts are considered loaded when registered
        });
        
        // Also register by key for programmatic access
        if (fontKey !== displayName) {
            fontManager.registerFont({
                name: fontKey,
                family: fontFamily,
                loaded: true
            });
        }
    });
}

function getClipCreatorFontValue(fontName: string): FontValue | null {
    const fontOption = FONT_OPTIONS.find(option => option.value === fontName || option.label === fontName);
    return fontOption ? fontOption.value : null;
}

/**
 * Initialize timeloom font system with clipCreator fonts
 */
export function initializeTimeloomFontSystem(fontNames: string[] = []): Promise<FontLoadResult[]> {
    const clipCreatorFontValuesToLoad = fontNames
        .map(getClipCreatorFontValue)
        .filter((fontValue): fontValue is FontValue => !!fontValue);
    loadClipCreatorFontsByValue(clipCreatorFontValuesToLoad);

    // Register them in the font manager
    registerClipCreatorFonts();
    
    // Return success for all registered fonts
    const results: FontLoadResult[] = Object.keys(CLIP_CREATOR_FONT_MAP).map(fontKey => ({
        success: true,
        fontFamily: CLIP_CREATOR_FONT_MAP[fontKey as FontValue]
    }));
    
    return Promise.resolve(results);
}

/**
 * Get enhanced font family resolution that includes clipCreator fonts
 */
export function resolveEnhancedFontFamily(fontName: string): string {
    // First try the integrated font map
    if (INTEGRATED_FONT_MAP[fontName]) {
        return INTEGRATED_FONT_MAP[fontName];
    }
    
    // Fallback to the font manager
    return fontManager.resolveFontFamily(fontName);
}

/**
 * Get all available fonts including clipCreator fonts
 */
export function getAllAvailableFonts(): Array<{ name: string; family: string; category: string }> {
    const clipCreatorFonts = FONT_OPTIONS.map(option => ({
        name: option.label,
        family: option.fontFamily,
        category: 'google-fonts'
    }));
    
    const systemFonts = Object.entries(DEFAULT_FONT_MAP).map(([name, family]) => ({
        name,
        family,
        category: 'system'
    }));
    
    return [...clipCreatorFonts, ...systemFonts];
}

/**
 * Get font suggestions based on category
 */
export function getFontsByCategory(category: keyof typeof FONT_CATEGORIES): FontConfig[] {
    const categoryFonts = FONT_CATEGORIES[category];
    return fontManager.getAvailableFonts().filter(font => 
        categoryFonts.includes(font.name as typeof categoryFonts[number])
    );
}

/**
 * Professional typography utilities
 */

export function calculateLineHeight(fontSize: number, style: 'tight' | 'normal' | 'loose' = 'normal'): number {
    const multipliers = {
        tight: 1.2,
        normal: 1.4,
        loose: 1.6
    };
    return fontSize * multipliers[style];
}

export function calculateLetterSpacing(fontSize: number, style: 'tight' | 'normal' | 'loose' = 'normal'): number {
    const multipliers = {
        tight: -0.02,
        normal: 0,
        loose: 0.05
    };
    return fontSize * multipliers[style];
}

/**
 * Font performance optimization
 */

export function preloadFonts(fontNames: string[]): void {
    // Preload fonts in the background
    fontNames.forEach(fontName => {
        fontManager.loadFont(fontName).catch(error => {
            console.warn(`Failed to preload font ${fontName}:`, error);
        });
    });
}

/**
 * Font validation utilities
 */

export function validateFontAvailability(fontName: string): Promise<boolean> {
    return fontManager.loadFont(fontName).then(result => result.success);
}

export function getFontLoadingStatus(): { [fontName: string]: boolean } {
    const fonts = fontManager.getAvailableFonts();
    const status: { [fontName: string]: boolean } = {};
    
    fonts.forEach(font => {
        status[font.name] = fontManager.isFontLoaded(font.name);
    });
    
    return status;
}

// Export types for TypeScript usage
export type { FontConfig, TextMeasurement, WordPosition, FontLoadResult, ClipCreatorFontConfig };
